import { Buffer } from 'buffer';
import { useState, useEffect, useCallback, useRef } from 'react';
import { PublicKey, Transaction, TransactionInstruction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';

const MEMO_PROGRAM_ID = new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr');
import { WALLET_ADDRESSES } from '../config/gameConfigs';

export const PHASE = {
  WAITING: 'waiting',
  COUNTDOWN: 'countdown',
  ENTERING: 'entering',
  WALKING: 'walking',
  RESULT: 'result',
};

const WINNER_SHARE = 1.0;
const RESULT_DISPLAY_SECONDS = 15;
const MIN_WANDER_SECONDS = 14;
const MAX_WANDER_SECONDS = 24;

export function useGameState(publicKey, gameConfig) {
  const config = gameConfig || {
    totalSquares: 100,
    gridSize: 10,
    localStorageKey: 'chickenDrop_mega',
    poolShare: 0.80,
    devShare: 0.10,
    bonanzaShare: 0.10,
    entryFee: 0.02,
    directBuyFee: 0.025,
  };

  const [phase, setPhase] = useState(PHASE.WAITING);
  const [squares, setSquares] = useState({});
  const squaresRef = useRef(squares);
  useEffect(() => { squaresRef.current = squares; }, [squares]);
  const [squareOwners, setSquareOwners] = useState({});
  const [winningSquare, setWinningSquare] = useState(null);
  const [chickenPos, setChickenPos] = useState(0);
  const [walkPath, setWalkPath] = useState([]);
  const [log, setLog] = useState(['Welcome to Chicken Drop! Play Chicken Droppings to earn your square.']);
  const [txStatus, setTxStatus] = useState(null);
  const [roundNum, setRoundNum] = useState(1);
  const [resultMessage, setResultMessage] = useState('');
  const [resultCountdown, setResultCountdown] = useState(RESULT_DISPLAY_SECONDS);
  const [wanderSeconds, setWanderSeconds] = useState(0);
  const [paidSquaresSold, setPaidSquaresSold] = useState(0);
  const [poolTotal, setPoolTotal] = useState(0);
  const [oraclePool, setOraclePool] = useState(null);
  const [settledPrize, setSettledPrize] = useState(null);

  const fetchOnChainPoolBalance = useCallback(async (connection) => {
    try {
      const { WALLET_ADDRESSES } = await import('../config/gameConfigs');
      const { PublicKey } = await import('@solana/web3.js');
      const poolAddress = WALLET_ADDRESSES.pools[config.id];
      if (!poolAddress || !connection) return;
      const pubkey = new PublicKey(poolAddress);
      const balance = await connection.getBalance(pubkey);
      const solBalance = balance / 1e9;
      setPoolTotal(solBalance);
    } catch (err) {
      console.error('Failed to fetch pool balance:', err);
    }
  }, [config]);

  const resultTimerRef = useRef(null);
  const walkRef = useRef(null);
  const roundTriggerRef = useRef(null);

  const addLog = useCallback((msg) => {
    setLog(prev => [...prev.slice(-20), msg]);
  }, []);

  const walletColor = useCallback((address) => {
    if (!address) return '#888';
    const colors = ['#E05A5A','#5AE0A0','#5AAAE0','#E0A05A','#A05AE0','#E05AA0','#5AE0E0','#E0E05A'];
    let hash = 0;
    for (let i = 0; i < address.length; i++) hash = address.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  }, []);

  const startNewRound = useCallback(() => {
    if (resultTimerRef.current) clearInterval(resultTimerRef.current);
    if (walkRef.current) clearInterval(walkRef.current);
    if (roundTriggerRef.current) clearTimeout(roundTriggerRef.current);

    setSquares({});
    setSquareOwners({});
    setWinningSquare(null);
    setWalkPath([]);
    setChickenPos(0);
    setResultMessage('');
    setResultCountdown(RESULT_DISPLAY_SECONDS);
    setWanderSeconds(0);
    setPaidSquaresSold(0);
    setSettledPrize(null);
    setPhase(PHASE.WAITING);
    setRoundNum(prev => prev + 1);
    addLog('--- New round started! Squares are being assigned. ---');
  }, [addLog]);

  const startWalking = useCallback((currentSquares, winner) => {
    const wanderTime = MIN_WANDER_SECONDS + Math.random() * (MAX_WANDER_SECONDS - MIN_WANDER_SECONDS);
    const wanderSteps = Math.floor(wanderTime / 2.0);

    const randomSquares = [];
    for (let i = 0; i < wanderSteps; i++) {
      randomSquares.push(Math.floor(Math.random() * config.totalSquares) + 1);
    }
    const fullPath = [...randomSquares, winner];

    setWalkPath(fullPath);
    setWanderSeconds(Math.round(wanderTime));
    setChickenPos(0);
    setPhase(PHASE.ENTERING);
    addLog('🐔 The chicken is approaching the arena!');

    roundTriggerRef.current = setTimeout(() => {
      setPhase(PHASE.WALKING);
      addLog('🐔 The chicken has entered the arena!');

      let step = 0;
      walkRef.current = setInterval(() => {
        step++;
        setChickenPos(step);

        if (step >= fullPath.length) {
          clearInterval(walkRef.current);
          setTimeout(() => {
            setWinningSquare(winner);
            setPhase(PHASE.RESULT);

            addLog(`🥚 Golden egg on square #${winner}!`);

            setTimeout(() => {
              let countdown = RESULT_DISPLAY_SECONDS;
              setResultCountdown(countdown);
              resultTimerRef.current = setInterval(() => {
                countdown--;
                setResultCountdown(countdown);
                if (countdown <= 0) {
                  clearInterval(resultTimerRef.current);
                  setPoolTotal(0);
                  startNewRound();
                }
              }, 1000);
            }, 4000);
          }, 800);
        }
      }, 2000);
    }, 3000);
  }, [addLog, startNewRound]);

  const startGame = useCallback((currentSquares) => {
    const ownedNums = Object.keys(currentSquares).map(Number);
    if (ownedNums.length === 0) {
      addLog('No squares assigned yet — waiting for players from Chicken Droppings.');
      startNewRound();
      return;
    }

    const winner = ownedNums[Math.floor(Math.random() * ownedNums.length)];
    addLog(`--- Round ${roundNum} starting! ${ownedNums.length} squares sold ---`);
    setPhase(PHASE.COUNTDOWN);

    let count = 2;
    const cdInterval = setInterval(() => {
      count--;
      if (count <= 0) {
        clearInterval(cdInterval);
        startWalking(currentSquares, winner);
      }
    }, 1000);
  }, [roundNum, addLog, startNewRound, startWalking]);

  const buySquare = useCallback(async (squareNum, walletAddress, sendTransaction, connection, isPaid = true, entryType = 'game') => {
    if (phase !== PHASE.WAITING) {
      addLog('Round in progress — wait for the next round!');
      return false;
    }
    if (squaresRef.current[squareNum]) {
      addLog(`Square #${squareNum} is already taken!`);
      return false;
    }

    const effectiveWallet = walletAddress || null;
    setTxStatus('pending');
    addLog(`Assigning square #${squareNum}...`);

    try {
      // Only charge real SOL for the first direct buy click (isPaid=true, entryType='direct')
      if (isPaid && entryType === 'direct' && sendTransaction && connection && effectiveWallet) {
        try {
          const fee = config.directBuyFee;
          const amountLamports = Math.round(fee * LAMPORTS_PER_SOL);
          const poolShare = Math.round(amountLamports * config.poolShare);
          const devShare = Math.round(amountLamports * config.devShare);
          const bonanzaShare = amountLamports - poolShare - devShare;
          const poolWallet = new PublicKey(WALLET_ADDRESSES.pools[config.id]);
          const devWallet = new PublicKey(WALLET_ADDRESSES.dev);
          const bonanzaWallet = new PublicKey(WALLET_ADDRESSES.bonanza);
          const playerKey = new PublicKey(effectiveWallet);
          const memoIx = new TransactionInstruction({
            keys: [{ pubkey: playerKey, isSigner: true, isWritable: false }],
            programId: MEMO_PROGRAM_ID,
            data: Buffer.from(`CD|${config.id}|${squareNum}`, 'utf8'),
          });
          const transaction = new Transaction().add(
            SystemProgram.transfer({ fromPubkey: playerKey, toPubkey: poolWallet, lamports: poolShare }),
            SystemProgram.transfer({ fromPubkey: playerKey, toPubkey: devWallet, lamports: devShare }),
            SystemProgram.transfer({ fromPubkey: playerKey, toPubkey: bonanzaWallet, lamports: bonanzaShare }),
            memoIx
          );
          const { blockhash } = await connection.getLatestBlockhash();
          transaction.recentBlockhash = blockhash;
          transaction.feePayer = playerKey;
          const signature = await sendTransaction(transaction, connection);
          await connection.confirmTransaction(signature, 'confirmed');
          console.log('Direct buy payment confirmed:', signature);
        } catch (err) {
          console.error('Direct buy payment failed:', err);
          addLog(`✗ Payment failed: ${err.message || 'Transaction rejected or insufficient SOL'}`);
          setTxStatus('error');
          setTimeout(() => setTxStatus(null), 3000);
          return false;
        }
      } else if ((entryType === 'free' || entryType === 'bonus') && sendTransaction && connection && effectiveWallet) {
        try {
          const playerKey = new PublicKey(effectiveWallet);
          const poolWallet = new PublicKey(WALLET_ADDRESSES.pools[config.id]);
          const memoIx = new TransactionInstruction({
            keys: [{ pubkey: playerKey, isSigner: true, isWritable: false }],
            programId: MEMO_PROGRAM_ID,
            data: Buffer.from(`CD|${config.id}|${entryType}|${squareNum}`, 'utf8'),
          });
          const transaction = new Transaction().add(
            SystemProgram.transfer({ fromPubkey: playerKey, toPubkey: poolWallet, lamports: 1 }),
            memoIx
          );
          const { blockhash } = await connection.getLatestBlockhash();
          transaction.recentBlockhash = blockhash;
          transaction.feePayer = playerKey;
          const signature = await sendTransaction(transaction, connection);
          await connection.confirmTransaction(signature, 'confirmed');
        } catch (err) {
          console.warn(`${entryType} marker tx failed (assigning square anyway):`, err);
        }
      } else {
        // Non-payment square assignments still get small delay for UX
        await new Promise(res => setTimeout(res, 400));
      }
      const shortAddr = effectiveWallet.slice(0, 4) + '...' + effectiveWallet.slice(-4);

      const newSquares = {
        ...squaresRef.current,
        [squareNum]: {
          owner: shortAddr,
          walletAddress: effectiveWallet,
          color: walletColor(effectiveWallet),
        }
      };

      squaresRef.current = newSquares;
      setSquares(newSquares);

      setSquareOwners(prev => {
        const updated = { ...prev, [squareNum]: { walletAddress: effectiveWallet, assignedAt: Date.now() } };
        return updated;
      });
      setTxStatus('confirmed');
      if (isPaid) {
        setPaidSquaresSold(prev => prev + 1);
      }
      addLog(`✓ ${shortAddr} assigned square #${squareNum}`);
      setTimeout(() => setTxStatus(null), 2000);

      return true;
    } catch (err) {
      setTxStatus('error');
      addLog(`✗ Assignment failed: ${err.message}`);
      setTimeout(() => setTxStatus(null), 3000);
      return false;
    }
  }, [phase, walletColor, addLog, startGame]);

  const bulkFillSquares = useCallback((squaresMap) => {
    setSquares(prev => {
      const merged = { ...prev, ...squaresMap };
      return merged;
    });
    setSquareOwners(prev => {
      const newOwners = {};
      Object.keys(squaresMap).forEach(num => {
        newOwners[num] = { walletAddress: squaresMap[num].walletAddress, assignedAt: Date.now() };
      });
      const merged = { ...prev, ...newOwners };
      return merged;
    });
  }, [addLog, startGame]);

  const triggerResult = useCallback((oracleWinner, oracleWallet, oracleLamports) => {
    if (typeof oracleWinner !== 'number') {
      console.warn('[triggerResult] called without oracleWinner — falling back to random local square');
    }
    if (resultTimerRef.current) clearInterval(resultTimerRef.current);
    if (walkRef.current) clearInterval(walkRef.current);
    if (roundTriggerRef.current) clearTimeout(roundTriggerRef.current);

    let winningSq;
    if (typeof oracleWinner === 'number') {
      winningSq = oracleWinner;
    } else {
      const squareNumbers = Object.keys(squares).map(Number).filter(n => squares[n]);
      winningSq = squareNumbers.length > 0
        ? squareNumbers[Math.floor(Math.random() * squareNumbers.length)]
        : Math.floor(Math.random() * config.totalSquares) + 1;
    }

    const prize = typeof oracleLamports === 'number'
      ? (oracleLamports / 1e9).toFixed(3)
      : poolTotal.toFixed(3);

    const localWinnerInfo = squares[winningSq];
    const winnerWallet = localWinnerInfo?.walletAddress || oracleWallet || null;

    if (publicKey && winnerWallet === publicKey) {
      setResultMessage(`Congratulations! You Won ${prize} SOL!`);
    } else if (publicKey && Object.values(squares).some(s => s.walletAddress === publicKey)) {
      setResultMessage(`😔 Better luck next time! The chicken chose square #${winningSq}`);
    } else {
      setResultMessage(`🥚 Square #${winningSq} wins! ${prize} SOL prize!`);
    }

    setSettledPrize(prize);
    startWalking(squares, winningSq);
  }, [squares, publicKey, poolTotal, startWalking]);

  useEffect(() => {
    return () => {
      if (resultTimerRef.current) clearInterval(resultTimerRef.current);
      if (walkRef.current) clearInterval(walkRef.current);
      if (roundTriggerRef.current) clearTimeout(roundTriggerRef.current);
    };
  }, []);

  const squaresSold = Object.keys(squares).length;
  const totalPot = squaresSold;

  return {
    phase, squares, squareOwners, winningSquare, chickenPos,
    walkPath, log, txStatus, roundNum, resultMessage,
    resultCountdown, wanderSeconds, squaresSold, paidSquaresSold, poolTotal,
    oraclePool, setOraclePool, settledPrize,
    displayPool: oraclePool !== null ? oraclePool : poolTotal,
    totalPot,
    buySquare, bulkFillSquares, startNewRound, triggerResult,
    fetchOnChainPoolBalance,
    walletColor,
    addLog,
  };
}
